import React, { useEffect } from "react";
import {
    Alert,
    Paper,
    Skeleton,
    Typography,
    Box,
    Chip,
    Divider,
    Button,
    Grid,
} from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import { usePncpPurchaseContractDetailController } from "./context/pncp-purchase-contract-detail-controller";
import { usePncpPurchaseContractDetailState } from "./context/pncp-purchase-contract-detail-context";
import PageHeader from "../components/shared/page-header";
import { useParams } from "react-router-dom";
import { useNavigate } from "../../hooks/useNavigate";
import dayjs from "dayjs";

const formatDate = (value?: string) => (value ? dayjs(value).format('DD/MM/YYYY') : '-');

const formatCurrency = (value?: number) =>
    value == null ? '-' : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDocument = (document?: string) => {
    const cleaned = (document || '').replace(/\D/g, '');
    if (cleaned.length === 14) return cleaned.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (cleaned.length === 11) return cleaned.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return document || '-';
};

const openExternal = (url?: string) => {
    if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
};

function InfoField({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {icon}
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                {children}
            </Box>
        </Box>
    );
}

export function PncpPurchaseContractDetailComponent() {
    const { loadContractDetail } = usePncpPurchaseContractDetailController();
    const { contractDetail, loading, error } = usePncpPurchaseContractDetailState();
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (id) {
            loadContractDetail(Number(id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const contract = contractDetail?.contract;
    const title = contract
        ? contract.supplierName || `Contrato ${contract.contractEffortNumber || `${contract.sequence}/${contract.yearContract}`}`
        : (loading ? 'Carregando...' : 'Contrato');
    const documents = contractDetail?.documents ?? [];
    const terms = contractDetail?.terms ?? [];
    const values = contract
        ? [
            { label: 'Valor Inicial', value: contract.initialValue },
            { label: 'Valor Global', value: contract.globalValue },
            { label: 'Valor da Parcela', value: contract.parcelValue },
            { label: 'Valor Acumulado', value: contract.accumulatedValue },
        ].filter((item) => item.value != null)
        : [];

    const backButton = (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/contratos-aquisicoes')}
                size="large"
            >
                Voltar para Contratos e Aquisições
            </Button>
        </Box>
    );

    return (
        <Box>
            <PageHeader
                title={title}
                subtitle=""
                breadcrumbs={[
                    { label: 'Contratos e Aquisições', href: '/contratos-aquisicoes' },
                    { label: title }
                ]}
            />

            {loading ? (
                <Box>
                    <Skeleton variant="rounded" width="100%" height={400} sx={{ borderRadius: 2, mb: 3 }} />
                    <Skeleton variant="text" width="60%" height={60} sx={{ mb: 2 }} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="100%" height={30} />
                    <Skeleton variant="text" width="80%" height={30} />
                </Box>
            ) : error || !contract ? (
                <React.Fragment>
                    <Alert severity={error ? 'error' : 'info'} sx={{ mb: 3 }}>
                        {error || 'Contrato não encontrado.'}
                    </Alert>
                    {backButton}
                </React.Fragment>
            ) : (
                <React.Fragment>
                    <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip icon={<VerifiedIcon />} label="PNCP" color="success" size="medium" sx={{ fontWeight: 600 }} />
                        {contract.situationContract && (
                            <Chip label={contract.situationContract} color="info" size="medium" sx={{ fontWeight: 600 }} />
                        )}
                        {contractDetail?.externalUrl && (
                            <Button
                                variant="outlined"
                                endIcon={<OpenInNewIcon />}
                                onClick={() => openExternal(contractDetail.externalUrl)}
                                sx={{ textTransform: 'none', ml: { sm: 'auto' } }}
                            >
                                Ver no PNCP
                            </Button>
                        )}
                    </Box>

                    <Paper sx={{ p: 4, mb: 3 }} variant="outlined">
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                            Informações do Contrato
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <InfoField icon={<BusinessIcon color="primary" />} label="Empresa Contratada">
                                    <Typography variant="body1" fontWeight={500}>{contract.supplierName || '-'}</Typography>
                                </InfoField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoField icon={<DescriptionIcon color="primary" />} label="CNPJ/CPF">
                                    <Typography variant="body1" fontWeight={500}>{formatDocument(contract.supplierNumberIdentification)}</Typography>
                                </InfoField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoField icon={<DescriptionIcon color="primary" />} label="Número do Contrato">
                                    <Typography variant="body1" fontWeight={500}>
                                        {contract.contractEffortNumber || `${contract.sequence}/${contract.yearContract}`}
                                    </Typography>
                                </InfoField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoField icon={<DescriptionIcon color="primary" />} label="Número do Processo">
                                    <Typography variant="body1" fontWeight={500}>{contract.processNumber || '-'}</Typography>
                                </InfoField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoField icon={<CalendarTodayIcon color="primary" />} label="Vigência">
                                    <Typography variant="body1" fontWeight={500}>
                                        {formatDate(contract.effectiveDateStart)} a {formatDate(contract.effectiveDateEnd)}
                                    </Typography>
                                </InfoField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <InfoField icon={<CalendarTodayIcon color="primary" />} label="Data de Assinatura">
                                    <Typography variant="body1" fontWeight={500}>{formatDate(contract.signDate)}</Typography>
                                </InfoField>
                            </Grid>

                            {values.map((item) => (
                                <Grid item xs={12} md={6} key={item.label}>
                                    <InfoField icon={<AttachMoneyIcon color="primary" />} label={item.label}>
                                        <Typography variant="body1" fontWeight={500}>{formatCurrency(item.value)}</Typography>
                                        {item.label === 'Valor da Parcela' && contract.numberOfInstallments != null && (
                                            <Typography variant="caption" color="text.secondary">
                                                {contract.numberOfInstallments} parcela(s)
                                            </Typography>
                                        )}
                                    </InfoField>
                                </Grid>
                            ))}

                            {contract.numeroControlePNCP && (
                                <Grid item xs={12} md={6}>
                                    <InfoField icon={<VerifiedIcon color="primary" />} label="Número de Controle PNCP">
                                        <Typography variant="body1" fontWeight={500} sx={{ wordBreak: 'break-all' }}>
                                            {contract.numeroControlePNCP}
                                        </Typography>
                                    </InfoField>
                                </Grid>
                            )}

                            {contract.object && (
                                <Grid item xs={12}>
                                    <Divider sx={{ my: 2 }} />
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Objeto
                                    </Typography>
                                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                                        {contract.object}
                                    </Typography>
                                </Grid>
                            )}

                            {contract.notes && (
                                <Grid item xs={12}>
                                    <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                                        Informações Complementares
                                    </Typography>
                                    <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                                        {contract.notes}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Paper>

                    {terms.length > 0 && (
                        <Paper sx={{ p: 4, mb: 3 }} variant="outlined">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <HistoryEduIcon color="primary" />
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    Termos do Contrato ({terms.length})
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {terms.map((term) => (
                                    <Grid item xs={12} key={term.sequentialContractTerm}>
                                        <Paper sx={{ p: 2, backgroundColor: 'background.default' }} elevation={0}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {term.contractTermType || 'Termo'} {term.contractTermNumber || term.sequentialContractTerm}
                                                </Typography>
                                                {term.signDate && (
                                                    <Chip label={`Assinado em ${formatDate(term.signDate)}`} size="small" variant="outlined" />
                                                )}
                                            </Box>
                                            {term.object && (
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    {term.object}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Vigência: {formatDate(term.effectiveDateStart)} a {formatDate(term.effectiveDateEnd)}
                                            </Typography>
                                            {(term.increaseValue != null || term.finalValue != null) && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {term.increaseValue != null && `Acréscimo: ${formatCurrency(term.increaseValue)}`}
                                                    {term.increaseValue != null && term.finalValue != null && ' · '}
                                                    {term.finalValue != null && `Valor final: ${formatCurrency(term.finalValue)}`}
                                                </Typography>
                                            )}
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    )}

                    {documents.length > 0 && (
                        <Paper sx={{ p: 4, mb: 3 }} variant="outlined">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                <AttachFileIcon color="primary" />
                                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                    Documentos do Contrato ({documents.length})
                                </Typography>
                            </Box>

                            <Grid container spacing={2}>
                                {documents.map((doc) => (
                                    <Grid item xs={12} key={doc.number}>
                                        <Paper
                                            sx={{
                                                p: 2,
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                gap: 2,
                                                flexWrap: 'wrap',
                                                backgroundColor: 'background.default',
                                                transition: 'all 0.2s',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                    transform: 'translateX(4px)',
                                                }
                                            }}
                                            elevation={0}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                                                <DescriptionIcon color="primary" />
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body1" fontWeight={500}>
                                                        {doc.title || doc.documentTypeDescription || `Documento ${doc.number}`}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {[doc.documentTypeDescription, doc.publishDate && `Publicado em ${formatDate(doc.publishDate)}`]
                                                            .filter(Boolean)
                                                            .join(' · ')}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            {doc.fileUrl && (
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<OpenInNewIcon />}
                                                    onClick={() => openExternal(doc.fileUrl)}
                                                    size="small"
                                                >
                                                    Abrir
                                                </Button>
                                            )}
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    )}

                    {backButton}
                </React.Fragment>
            )}
        </Box>
    );
}
